<script setup>
import { get_traits } from '@/services/traits';
import { delete_material, create_material, get_material, get_reading_materials, update_material } from '@/services/materials';
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { swalError } from '@/helper';
import { get_characteristics, get_packages } from '@/services/option';

const route = useRoute()
const isLoading = ref(true)
const material = ref({
  id: '',
  name: '',
  description: '',
  reference_urls: [],
})
const selectedFile = ref(null)
const reading_materials = ref({list: [], temp: {link: '', title: ''}})
const selectedTrait = ref([])
const selectedTraitConfig = ref([])
const traits = ref([])
onMounted(async ()=>{
  await fetchData()
})

async function fetchData() {
  isLoading.value = true
  let traits_response = await get_traits()
  traits.value = generateTraitOption(traits_response.result.data)
  material.value.id = route.params.id
  let moduleResponse = await get_material(material.value.id)
  material.value = parseModule(moduleResponse.result.data)
  let readingMaterial = await get_reading_materials(route.params.id)
  reading_materials.value['list_old'] = parseReadingMaterials(readingMaterial.result.data.data)
  await getPackages()
  isLoading.value = false
}

function parseReadingMaterials(response){
  let tempReading = response.map(item => {
    let data = {
      ...item.attributes,
      id: item.id
    }
    if (!data.description && data.material_media) {
      data.description = data.material_media
    }
    return data
  })
  return tempReading
}

function parseModule(response){
  response.included.map(item => {
    if (item.attributes.object_type == 'trait'){
      selectedTraitConfig.value.push({id: item.attributes.object_id, meta_id: item.id})
      selectedTrait.value.push(item.attributes.object_id)
    }
    if (item.attributes.object_type == 'package'){
      selectedPackage.value = item.attributes.object_id
    }
  })

  const tempModule = {...response.data.attributes, id: response.data.id}
  tempModule.reference_urls = JSON.parse(tempModule.description)
  return tempModule
}

function generateTraitOption(reponse){
  reponse = reponse.map(item => {
    return {
      valueProp: item.id,
      label: item.name,
      disabled: false
    }
  })
  return reponse
}

function handleFileSelect(event) {
  const file = event.target.files[0];
  if (file) {
    selectedFile.value = file;
    material.value.document_name = file.name; // Update display name
  }
}

function addReading(){
  if (reading_materials.value.temp.link && reading_materials.value.temp.title) {
    reading_materials.value.list.push({
      title: reading_materials.value.temp.title,
      link: reading_materials.value.temp.link
    });
    reading_materials.value.temp = {link: '', title: ''};
  } else {
    swalError('Please fill in both file name and link.');
  }
}

function removeReading(index){
  reading_materials.value.list.splice(index, 1);
}

function removeReadingOld(index){
  const swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      confirmButton: "btn btn-success ms-2",
      cancelButton: "btn btn-danger"
    },
    buttonsStyling: false
  });
  swalWithBootstrapButtons.fire({
    title: "Are you sure?",
    text: "You won't be able to revert this!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Delete it",
    cancelButtonText: "Cancel",
    reverseButtons: true
  }).then(async (result) => {
    if (result.isConfirmed) {
      await deleteReading(index)
      swalWithBootstrapButtons.fire({
        title: "Deleted!",
        text: "Your file has been deleted.",
        icon: "success"
      });
    } else if (
      /* Read more about handling dismissals below */
      result.dismiss === Swal.DismissReason.cancel
    ) {
      swalWithBootstrapButtons.fire({
        title: "Cancelled",
        text: "Your imaginary file is safe :)",
        icon: "error"
      });
    }
  });
}

async function deleteReading(index){
  let tempReading = reading_materials.value.list_old.at(index)
  let response = await delete_material(tempReading.id)
  if (!response.error){
    reading_materials.value.list_old.splice(index, 1);
  }
}

async function createReading(module_id){
  if (reading_materials.value.list.length !== 0){
    let materialParam = reading_materials.value.list.map(item => {
          return {
            name: item.title,
            material_type: 'reading_material',
            description: item.link, // Store link in description
            material_relationships_attributes: [{
              object_id: parseInt(module_id),
              object_type: 'material',
            }]
          }    })
    let storeResponse = []
    await Promise.all(materialParam.map(async (item) => {
      let response = await create_material(item)
      if (response.error){
        storeResponse.push(response)
      }
    }))
    let hasError = storeResponse.some(item => item.error == true)
    return {error: hasError ? true : false}
  }
  return {error: false}
}

const temp_url = ref({name: '', url: ''})
function addUrl(){
  material.value.reference_urls.push(temp_url.value)
  temp_url.value = {name: '', url: ''}
}

function removeUrl(index){
  material.value.reference_urls.splice(index, 1)
}

const isSubmitting = ref(false)
async function createMaterial(){
  let isPass = checkFrom()
  if (isPass.error){
    swalError(isPass.msg)
    return
  }
  isSubmitting.value = true
  // materialParams is now formData
  const formData = new FormData();
  formData.append('id', material.value.id);
  formData.append('material[name]', material.value.name);
  formData.append('material[description]', JSON.stringify(material.value.reference_urls));
  formData.append('material[material_type]', 'module');
  
  if (selectedFile.value) {
    formData.append('material[material_media]', selectedFile.value);
  }

  const relationships = serialTrait();
  relationships.forEach((rel, index) => {
    if (rel.id) formData.append(`material[material_relationships_attributes][${index}][id]`, rel.id);
    formData.append(`material[material_relationships_attributes][${index}][object_id]`, rel.object_id);
    formData.append(`material[material_relationships_attributes][${index}][object_type]`, rel.object_type);
    if (rel._destroy) formData.append(`material[material_relationships_attributes][${index}][_destroy]`, rel._destroy);
  });

  let response = await update_material(formData)
  
  isSubmitting.value = false
  if (response.error){
    swalError(response.result?.error, true)
  }else{
    // parse response info
    let readingResponse = await createReading(response.result.data.data.id) 
    if (readingResponse.error){
      swalError(response.result.error, true)
      return
    }
    
    let updateResponse = await updateReadingMaterials()
    if (updateResponse.error){
      swalError('Failed to update some reading materials', true)
      return
    }

    // Clear new reading materials list
    reading_materials.value.list = []
    
    // Reload data
    await fetchData()

    Swal.fire({
      title: 'Updated',
      text: "Module has been updated",
      icon: 'success'
    })
  }
}

async function updateReadingMaterials() {
  let storeResponse = []
  await Promise.all(reading_materials.value.list_old.map(async (item) => {
    let params = {
      id: item.id,
      name: item.name,
      description: item.description
    }
    let response = await update_material(params)
    if (response.error){
      storeResponse.push(response)
    }
  }))
  let hasError = storeResponse.some(item => item.error == true)
  return {error: hasError ? true : false}
}

function serialTrait(){
  const expected = [
    // Keep selected traits, mark removed ones with _destroy
    ...selectedTraitConfig.value.map(item => ({
      object_id: item.id,
      object_type: 'trait',
      id: item.meta_id,
      ...(removedTrait.value.includes(item.id) && { _destroy: true })
    })),

    // Add new traits
    ...newTrait.value.map(id => ({
        object_id: id,
        object_type: 'trait'
      }))
  ];

  return expected
}

const removedTrait = ref([])
const newTrait = ref([])
function removeTrait(option){
  if (selectedTraitConfig.value.find(item=>item.id == option)){
    removedTrait.value.push(option)
  }
}

function addTrait(option){
  if (!newTrait.value.find(item=>item == option)){
    newTrait.value.push(option)
  }
}

function checkFrom(){
  let isPass = {
    msg: '',
    error: false
  }

  if (reading_materials.value.temp.title != '' || reading_materials.value.temp.link != ''){
    isPass.msg = 'Check your reading material'
    isPass.error = true
  }else if(temp_url.value.name != '' || temp_url.value.url != ''){
    isPass.msg = 'Check your reference url'
    isPass.error = true
  }else if(selectedTrait.value.length == 0){
    isPass.msg = 'Check your trait selection'
    isPass.error = true
  }
  return isPass
}

function clearFileInput(){
  document.getElementById('pdf_module_input').value = null
  selectedFile.value = null
  // material.value.material_media = ''; // Do not clear existing URL until saved? Or clear it to indicate removal?
  // Current implementation doesn't support 'removing' file without replacing or deleting module.
}

const uploadProgress = ref({}); // To store progress for each file
const isUploading = ref(false); // To indicate if any file is currently uploading
const uploadedFiles = ref([]); // To store S3 URLs after successful upload
const selectedPackage = ref('')
const packageConfig = ref([])
async function getPackages(){
  let response = await get_packages()
  if (response.error){
    swalError(response.result.error, true)
    return
  }
  packageConfig.value = [
    ...response.result.map(item=>({
      valueProp: item.id,
      label: item.options.name.value,
      disabled: false
    }))
  ]
}

async function updateTraitSelection(value, select){
  // remove old trait from trait selection
  removedTrait.value = [...selectedTrait.value]
  selectedTrait.value = []
  // get new trait selection for the package
  let response = await get_characteristics(value)
  if (response.error){
    swalError(response.result.error)
    return
  }
  traits.value = [
    ...response.result.included.map(item => ({
      valueProp: item.id,
      label: item.attributes.name,
      disabled: false
    }))
  ]
}
</script>

<template>
  <div class="container-fluid">
    <Breadcrumbs main="Edit Module" title="Edit Reading Material"/>

    <LoadingCard v-if="isLoading" />
    <template v-else>

      <div class="row mt-3">
        <div class="col-9 card">
          <div class="card-body">
            <div class="form-items mt-3">
              <!-- Title -->
              <div class="row">
                <div class="col-5">
                  <label class="form-label" for="name">Name</label>
                  <input v-model="material.name" class="form-control" id="name" type="text" placeholder="Module Name" required>
                </div>
              </div>
              <!-- PDF Module -->
              <div class="mt-4 row">
                <label class="form-label" for="age">PDF Module</label>
                <div class="row align-items-center">
                  <div class="col-md-9">
                    <div class="row align-items-center">
                      <div class="col-9">
                        <input 
                          class="form-control"
                          id="pdf_module_input"
                          type="file" 
                          aria-label="dna report" 
                          required=""
                          @change="handleFileSelect"
                          accept="application/pdf"
                        >
                      </div>
                      <button class="btn btn-light-danger btn-icon" @click="clearFileInput()">
                        <vue-feather type="trash" size="1rem"/>
                      </button>
                    </div>
                    <div class="mt-2 d-flex align-items-center">
                      <p class="mb-0" v-if="selectedFile">
                        <span class="text-secondary">Selected:</span> {{ selectedFile.name }}
                      </p>
                      <p class="mb-0" v-else>
                        <span class="text-secondary">Current:</span> {{ material.document_name || 'None' }}
                      </p>
                      <a v-if="material.material_media && !selectedFile" 
                         :href="material.material_media" 
                         target="_blank" 
                         class="ms-2 btn icon-btn btn-light-success"
                      >
                        <vue-feather type="download" size="1rem"/>
                      </a>
                    </div>
                  </div>
                  
                </div>
              </div>

              <!-- Reading Material -->
              <div class="mt-4 row">
                <label class="form-label" for="age">Reading Material</label>
                <div>
                  <form @submit.prevent="addReading()" class="row">
                    <div class="col-5">
                      <input v-model="reading_materials.temp.title" class="form-control" id="name" type="text" placeholder="File Name" required>
                    </div>
                    <div class="col-5">
                      <input v-model="reading_materials.temp.link" class="form-control" id="link" type="text" placeholder="File Link" required>
                    </div>
                    <div class="col-2">
                      <div class="col-2">
                        <button type="submit" class="btn-rounded-icon p-0 btn btn-success"
                        >
                          <vue-feather type="plus"></vue-feather>
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
                <div v-auto-animate class="reading-list mt-2">
                  <div v-for="(item, i) in reading_materials.list" class="row my-2">
                    <div class="col-5">
                      <input v-model="item.title" class="form-control" id="name" type="text" placeholder="File Name" required>
                    </div>
                    <div class="col-5">
                      <input v-model="item.link" class="form-control" id="link" type="text" placeholder="File Link" required>
                    </div>
                    <div class="col-2">
                      <div class="col-2">
                        <button @click="removeReading(i)" class="btn-rounded-icon p-0 btn btn-danger"
                        >
                          <vue-feather type="x"></vue-feather>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div v-for="(item, i) in reading_materials.list_old" class="row my-2">
                    <div class="col-5">
                      <input v-model="item.name" class="form-control" id="name" type="text" placeholder="File Name" required>
                    </div>
                    <div class="col-5">
                      <input v-model="item.description" class="form-control" id="link" type="text" placeholder="File Link" required>
                    </div>
                    <div class="col-2">
                      <div class="col-2">
                        <button @click="removeReadingOld(i)" class="btn-rounded-icon p-0 btn btn-danger"
                        >
                          <vue-feather type="x"></vue-feather>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- URL -->
              <div v-auto-animate class="mt-4">
                <div>
                  <label class="form-label">Reference URL</label>
                </div>
                <form @submit.prevent="addUrl()" class="row mt-3">
                  <div class="col-6">
                    <input class="form-control" 
                      type="text" 
                      placeholder="Title" required
                      v-model="temp_url.name"
                    >
                  </div>
                  <div class="col-4">
                    <input class="form-control" 
                      type="text" 
                      placeholder="Reference URL" required
                      v-model="temp_url.url"
                    >
                  </div>
                  <div class="col-2">
                    <button type="submit" class="btn-rounded-icon p-0 btn btn-success"
                    >
                      <vue-feather type="plus"></vue-feather>
                    </button>
                  </div>
                </form>
                <!-- items -->
                <div v-for="(reference_url, index) in material.reference_urls" class="row mt-2">
                  <div class="col-6">
                    <input class="form-control" 
                      type="text" 
                      placeholder="Reference Url" required
                      v-model="reference_url.name"
                    >
                  </div>
                  <div class="col-4">
                    <input class="form-control" 
                      type="text" 
                      placeholder="Reference Url" required
                      v-model="reference_url.url"
                    >
                  </div>
                  <div class="col-2">
                    <button class="btn-rounded-icon p-0 btn btn-danger"
                      @click="removeUrl(index)"
                    >
                      <vue-feather type="x"></vue-feather>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Submit & Trait selection -->
        <div class="col-3">
          <div class="card">
            <div class="card-body">
              
              <!-- Package Selection -->
              <div class="row mb-3">
                <label class="form-label" for="age_group_from">Packages</label>
                <multiselect 
                  style="padding-left: 0;"
                  v-model="selectedPackage" 
                  placeholder="Select Package"
                  :options="packageConfig"
                  valueProp="valueProp"
                  @change="updateTraitSelection"
                ></multiselect>
              </div>

              <!-- Trait Selection -->
              <div class="row mb-3">
                <label class="form-label" for="age_group_from">Trait</label>
                <multiselect 
                  @select="addTrait"
                  @deselect="removeTrait"
                  style="padding-left: 0;"
                  v-model="selectedTrait" 
                  placeholder="Select trait"
                  :options="traits"
                  :mode="'tags'"
                  valueProp="valueProp"
                  searchable="true"
                ></multiselect>
              </div>
              <!-- submit btn -->
              <button class="btn btn-light-success"
                @click="createMaterial()"
                :disabled="isSubmitting"
              >
                <div v-if="isSubmitting" class="spinner-border spinner-border-sm" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <template v-else>
                  Update Module
                </template>
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.form-label{
  font-weight: 500;
}
</style>